import { intlTagFor } from './locales.js';
/**
 * Locale-aware formatting. Everything user-facing goes through these rather
 * than a hard-coded `en-GH`, so a French or Kinyarwanda customer sees dates
 * and money the way they expect.
 */
/**
 * Minor units per major unit, by ISO-4217 exponent. Duplicated from
 * `@zuruflex/types` rather than imported, because `@zuruflex/i18n` sits underneath
 * the domain packages and must not depend on them.
 */
const ZERO_DECIMAL = new Set([
    'RWF', 'BIF', 'UGX', 'XOF', 'XAF', 'KMF', 'DJF', 'GNF',
    'JPY', 'KRW', 'VND', 'CLP', 'ISK', 'PYG', 'VUV',
]);
function decimalsFor(currency) {
    return ZERO_DECIMAL.has(currency.toUpperCase()) ? 0 : 2;
}
/**
 * `45000, 'RWF'` → `"45 000 FRw"`, not `"450,00 RWF"`.
 *
 * The exponent is per currency: dividing by 100 unconditionally would price
 * every Rwandan car at a hundredth of its value.
 */
export function formatMoneyIn(amount, currency, locale) {
    const decimals = decimalsFor(currency);
    return new Intl.NumberFormat(intlTagFor(locale), {
        style: 'currency',
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(amount / 10 ** decimals);
}
/**
 * The same money, shortened for a chart axis: `"RF 1.2M"`.
 *
 * An axis label has room for about six characters, and a currency that
 * counts in whole francs runs to seven digits by the middle of a good
 * month — spelled out in full, the labels either collide or shrink to
 * unreadable. Compact notation keeps the shape of the number, which is all
 * an axis is being asked for; the tooltip beside it still gives the exact
 * figure.
 */
export function formatMoneyCompactIn(amount, currency, locale) {
    const decimals = decimalsFor(currency);
    return new Intl.NumberFormat(intlTagFor(locale), {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: 1,
    }).format(amount / 10 ** decimals);
}
/**
 * The clock each market keeps. A time written for somebody in a market is
 * written on its clock, not the server's — a server in Kigali told an
 * owner in Accra to answer by 7:02 when it was 5:02 for them.
 */
export const MARKET_TIME_ZONES = {
    GH: 'Africa/Accra',
    RW: 'Africa/Kigali',
};
/** The market's zone, or undefined (the runtime's own) for one we do not know. */
export function timeZoneFor(countryCode) {
    return countryCode ? MARKET_TIME_ZONES[countryCode.toUpperCase()] : undefined;
}
export function formatDateIn(value, locale, timeZone) {
    return new Intl.DateTimeFormat(intlTagFor(locale), { dateStyle: 'medium', timeZone }).format(new Date(value));
}
export function formatDateTimeIn(value, locale, timeZone) {
    return new Intl.DateTimeFormat(intlTagFor(locale), {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone,
    }).format(new Date(value));
}
export function formatNumberIn(value, locale) {
    return new Intl.NumberFormat(intlTagFor(locale)).format(value);
}
/**
 * A star rating, from the centistars every rating column stores.
 *
 * `487` reads as "4.9" in English and "4,9" in French — one decimal, because
 * two implies a precision that forty opinions do not have, and none would
 * make 4.4 and 4.6 the same number.
 */
export function formatRatingIn(centistars, locale) {
    return new Intl.NumberFormat(intlTagFor(locale), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(centistars / 100);
}
